import type { Bytes } from "../types/Units"
import { Reduce } from "../functions/Reduce"

/**
 * Merges multiple Uint8Arrays or ArrayBuffers into a single Uint8Array.
 *
 * @param arrays - Array of Uint8Array or ArrayBuffer to merge
 * @param bytes - Optional total size; if provided skips computing length
 * @returns A new Uint8Array containing all input bytes concatenated
 *
 * @example
 * ```ts
 * import { UInt8Merge } from "@pdfking/tfunc"
 *
 * const a = new Uint8Array([1, 2, 3]);
 * const b = new Uint8Array([4, 5, 6]);
 * const merged = UInt8Merge([a, b]);
 * console.log(merged); // Uint8Array([1, 2, 3, 4, 5, 6])
 *
 * // Works with ArrayBuffers too
 * const buffer = new ArrayBuffer(2);
 * const view = new Uint8Array(buffer);
 * view[0] = 7;
 * view[1] = 8;
 * const result = UInt8Merge([a, buffer]);
 * console.log(result); // Uint8Array([1, 2, 3, 7, 8])
 *
 * // With known total size
 * const fast = UInt8Merge([a, b], 6);
 * ```
 */

const isBuffer = (value: ArrayBuffer | Uint8Array): value is ArrayBuffer =>
  value instanceof ArrayBuffer

const toArray = (value: ArrayBuffer | Uint8Array): Uint8Array =>
  isBuffer(value) ? new Uint8Array(value) : value

export const UInt8Merge = (
  arrays: ReadonlyArray<ArrayBuffer | Uint8Array>,
  bytes?: Bytes
): Uint8Array => {
  if (arrays.length === 1) return toArray(arrays[0])
  const totalLength =
    bytes ??
    Reduce(
      (sum, arr: ArrayBuffer | Uint8Array) =>
        sum + (isBuffer(arr) ? arr.byteLength : (arr as Uint8Array).length),
      arrays,
      0
    )
  const result = new Uint8Array(totalLength)
  let offset = 0
  for (const arr of arrays) {
    const view = toArray(arr)
    result.set(view, offset)
    offset += view.length
  }
  return result
}
